import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, MapPin, Calendar, Star, Users, Sparkles } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta } from "../components/shared/SEOHelper";

const WeekendYogaRetreatsUK = () => {
  usePageMeta({
    title: "Weekend Yoga Retreats UK | Short Yoga Breaks & Getaways",
    description: "Weekend yoga retreats across the UK. 2-3 night yoga breaks perfect for busy people needing rest and renewal.",
    canonical: "https://annelielily.com/retreats/weekend"
  });

  const whyWeekend = [
    {
      title: "Perfect Length",
      description: "Long enough to truly unwind and reset, short enough to fit into a busy life. You'll feel transformed without taking too much time away."
    },
    {
      title: "Complete Immersion",
      description: "Unlike day retreats, you sleep in the retreat environment. Waking up without an alarm, surrounded by peace — that's where the magic happens."
    },
    {
      title: "Deeper Practice",
      description: "Multiple yoga sessions over 2-3 days allow you to build on what you learn, going deeper than any single class or day could offer."
    },
    {
      title: "Genuine Rest",
      description: "With meals prepared for you, no chores, and no decisions to make, your nervous system finally gets to fully downshift."
    }
  ];

  const typicalSchedule = {
    friday: [
      { time: "3-5pm", activity: "Arrivals & settling in" },
      { time: "6pm", activity: "Welcome circle & light yoga" },
      { time: "7:30pm", activity: "Dinner" },
      { time: "9pm", activity: "Optional evening practice or free time" }
    ],
    saturday: [
      { time: "7:30am", activity: "Optional sunrise meditation" },
      { time: "8:30am", activity: "Breakfast" },
      { time: "10am", activity: "Morning yoga practice (90 mins)" },
      { time: "12pm", activity: "Free time / treatments" },
      { time: "1pm", activity: "Lunch" },
      { time: "2:30pm", activity: "Workshop / nature walk" },
      { time: "4:30pm", activity: "Afternoon yoga / yin" },
      { time: "6:30pm", activity: "Dinner" },
      { time: "8pm", activity: "Sound bath / fire circle" }
    ],
    sunday: [
      { time: "8am", activity: "Gentle yoga & meditation" },
      { time: "9:30am", activity: "Brunch" },
      { time: "11am", activity: "Closing circle" },
      { time: "12pm", activity: "Departures" }
    ]
  };

  const included = [
    "2-3 nights accommodation",
    "All meals (vegetarian/vegan)",
    "4-6 yoga sessions",
    "Meditation & breathwork",
    "Sound healing session",
    "Opening & closing ceremonies",
    "Free time for rest or nature",
    "Use of facilities (sauna, grounds)",
    "All props & mats provided"
  ];

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      <PageBreadcrumb currentPage="Weekend Retreats" currentSlug="retreats/weekend" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
              Weekend Yoga Retreats UK
            </h1>
            <p className="text-neutral-700 max-w-2xl mx-auto leading-relaxed">
              Can't get away for a full week? A weekend yoga retreat offers the perfect 
              balance — enough time to truly rest and reset, without requiring a major 
              schedule upheaval. Step away Friday, return Sunday transformed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Weekend */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Why Choose a Weekend Retreat?
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {whyWeekend.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/80 p-6 rounded-xl border border-gold/10"
              >
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Schedule */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Sample Weekend Retreat Schedule
            </h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              Every retreat is different, but here's what a typical Friday-Sunday yoga 
              weekend might look like.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { day: "Friday", schedule: typicalSchedule.friday },
              { day: "Saturday", schedule: typicalSchedule.saturday },
              { day: "Sunday", schedule: typicalSchedule.sunday }
            ].map((dayData, i) => (
              <motion.div
                key={dayData.day}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/80 rounded-2xl border border-gold/10 overflow-hidden"
              >
                <div className="bg-gold/10 px-5 py-3">
                  <h3 className="font-heading text-lg text-neutral-800">{dayData.day}</h3>
                </div>
                <div className="p-5">
                  <ul className="space-y-3">
                    {dayData.schedule.map((item, j) => (
                      <li key={j} className="flex gap-3 text-sm">
                        <span className="text-gold font-medium w-16 flex-shrink-0">{item.time}</span>
                        <span className="text-neutral-700">{item.activity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-neutral-500 text-sm mt-6 italic">
            Note: Schedules are flexible and may vary. Rest time is always available.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              What's Included in a Weekend Retreat
            </h2>
            <div className="bg-white/80 p-6 md:p-8 rounded-2xl border border-gold/10">
              <div className="grid sm:grid-cols-2 gap-3">
                {included.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-neutral-700">
                    <Check className="w-5 h-5 text-gold flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gold/10">
                <p className="text-neutral-600 text-sm">
                  <strong>Pricing typically ranges from £295-£450</strong> depending on 
                  accommodation type (shared/single) and location. Early bird discounts 
                  often available.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Weekend Retreats Are Perfect If You...
            </h2>
            <ul className="space-y-3">
              {[
                "Have a busy job and can't take a full week off",
                "Want more than a day retreat but aren't ready for a week-long commitment",
                "Need a proper reset but have family or work responsibilities",
                "Are new to retreats and want to try one without major investment",
                "Live within driving distance of retreat venues in the UK",
                "Value quality over quantity — intensive restoration in minimal time"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 bg-white/70 p-4 rounded-xl border border-gold/10">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-700">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              Weekend Retreat Locations
            </h2>
            <p className="text-neutral-700 text-center mb-8">
              Our weekend retreats take place at carefully selected venues chosen for 
              their peaceful atmosphere, beautiful surroundings, and quality facilities.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { region: "Somerset", desc: "Rolling hills, historic venues, easy access from Bristol & Bath" },
                { region: "Devon", desc: "Coastal retreats, countryside escapes, stunning landscapes" },
                { region: "Cotswolds", desc: "Quintessential English countryside, charming villages" },
                { region: "Wales", desc: "Mountains, wild beauty, deep peace and isolation" }
              ].map((location, i) => (
                <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-gold" />
                    <h3 className="font-heading text-lg text-neutral-800">{location.region}</h3>
                  </div>
                  <p className="text-neutral-600 text-sm">{location.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-neutral-500 text-sm mt-6">
              Specific venues are announced when retreat dates are released.
            </p>
          </motion.div>
        </div>
      </section>

      {/* vs Day vs Week */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              Day Retreat vs Weekend vs Week-Long
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full bg-white/80 rounded-2xl overflow-hidden">
                <thead>
                  <tr className="bg-gold/10">
                    <th className="px-4 py-3 text-left font-heading text-neutral-800">Feature</th>
                    <th className="px-4 py-3 text-center font-heading text-neutral-800">Day</th>
                    <th className="px-4 py-3 text-center font-heading text-neutral-800 bg-gold/20">Weekend</th>
                    <th className="px-4 py-3 text-center font-heading text-neutral-800">Week</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    { feature: "Duration", day: "6-8 hours", weekend: "2-3 nights", week: "5-7 nights" },
                    { feature: "Price range", day: "£85-95", weekend: "£295-450", week: "£599-899" },
                    { feature: "Time off needed", day: "1 day", weekend: "Fri-Sun", week: "Full week" },
                    { feature: "Yoga sessions", day: "2-3", weekend: "4-6", week: "10+" },
                    { feature: "Depth of rest", day: "Good", weekend: "Deep", week: "Transformative" },
                    { feature: "Travel required", day: "None", weekend: "Some", week: "Yes" },
                    { feature: "Best for", day: "Quick reset", weekend: "Proper break", week: "Life change" }
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white/50" : ""}>
                      <td className="px-4 py-3 font-medium text-neutral-800">{row.feature}</td>
                      <td className="px-4 py-3 text-center text-neutral-600">{row.day}</td>
                      <td className="px-4 py-3 text-center text-neutral-800 bg-gold/5 font-medium">{row.weekend}</td>
                      <td className="px-4 py-3 text-center text-neutral-600">{row.week}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/80 p-8 rounded-2xl border border-gold/10 text-center"
          >
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-gold text-gold" />
              ))}
            </div>
            <p className="text-neutral-700 italic text-lg mb-6">
              "I was skeptical that a weekend could make much difference, but I was wrong. 
              By Sunday morning, I felt like I'd had a full holiday. The combination of 
              yoga, delicious food, and complete rest was exactly what I needed. I've 
              booked my next one already."
            </p>
            <p className="font-medium text-neutral-800">Weekend Retreat Guest</p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ready to Book a Weekend Away?
            </h2>
            <p className="text-neutral-600 mb-6">
              Explore all our retreat options or register interest for upcoming weekend dates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/retreats/yoga-retreats-uk" className="btn-primary">
                View All Retreats <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
              <Link to="/contact" className="btn-secondary">
                Register Interest
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h3 className="font-heading text-lg text-neutral-800 mb-4 text-center">Related Pages</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/retreats/what-to-expect" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">What to Expect</span>
              <p className="text-neutral-500 text-xs mt-1">First retreat guide</p>
            </Link>
            <Link to="/retreats/packing-list" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Packing List</span>
              <p className="text-neutral-500 text-xs mt-1">What to bring</p>
            </Link>
            <Link to="/retreats/benefits" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Retreat Benefits</span>
              <p className="text-neutral-500 text-xs mt-1">Why go on retreat</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WeekendYogaRetreatsUK;
